import {
  type CreditNoteListItem,
  type CreditSourceType,
  type CustomerRefund,
  type CustomerRefundListItem,
  isZero,
  type Page,
  type RefundInput,
  type RefundSummary,
} from '@sahab/contracts'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type DownloadedFile } from '../../lib/api'
import type { Language } from '../../lib/format'
import { invalidateSales, type ReceiptListItem, salesKeys } from '../sales/queries'
import type { RefundListParams, RefundSummaryParams } from './search'

/**
 * Refunds live under the sales keys: a refund changes the receipts and credit notes it draws on, the
 * customer's statement and the aging, and any sales posting refreshes the refunds in turn.
 */
export const refundKeys = {
  all: [...salesKeys.all, 'refunds'] as const,
  list: (query: RefundListParams) => [...salesKeys.all, 'refunds', 'list', query] as const,
  summary: (query: RefundSummaryParams) => [...salesKeys.all, 'refunds', 'summary', query] as const,
  detail: (id: string) => [...salesKeys.all, 'refunds', 'detail', id] as const,
  credits: (customerId: string) => [...salesKeys.all, 'refunds', 'credits', customerId] as const,
}

/** The largest page the API serves: the newest receipts and credit notes of one customer with credit left. */
export const CREDITS_LIMIT = 200

const path = (id: string, action?: string) =>
  `/sales/refunds/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

function refundFilters(query: Omit<RefundListParams, 'page' | 'pageSize'>) {
  return {
    sort: query.sort,
    q: query.q,
    status: query.status,
    method: query.method,
    treasuryAccountId: query.treasuryAccountId,
    customerId: query.customerId,
    from: query.from,
    to: query.to,
  }
}

export function refundListQueryOptions(query: RefundListParams) {
  return queryOptions({
    queryKey: refundKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<Page<CustomerRefundListItem>>('/sales/refunds', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...refundFilters(query) },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Counts per view tab (and the amount paid out) for the list's other filters. */
export function refundSummaryQueryOptions(query: RefundSummaryParams) {
  return queryOptions({
    queryKey: refundKeys.summary(query),
    queryFn: ({ signal }) =>
      api.get<RefundSummary>('/sales/refunds/summary', { signal, query: { ...query } }),
    placeholderData: keepPreviousData,
  })
}

/** `GET /sales/refunds/export` (sales.export): the list's view, filters and sort as CSV. */
export function exportRefunds(query: RefundListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/sales/refunds/export', {
    query: { format: 'csv', lang: language, ...refundFilters(query) },
    fallbackName: 'refunds.csv',
  })
}

export function refundQueryOptions(id: string) {
  return queryOptions({
    queryKey: refundKeys.detail(id),
    queryFn: ({ signal }) => api.get<CustomerRefund>(path(id), { signal }),
  })
}

/** A receipt's or credit note's remainder the customer may be paid back. */
export interface CustomerCredit {
  sourceType: CreditSourceType
  id: string
  number: string
  date: string
  total: string
  refunded: string
  /** What is left: total − applied to invoices − already refunded. */
  available: string
}

export interface CustomerCredits {
  credits: CustomerCredit[]
  /** More posted receipts or credit notes exist than one page holds (the oldest are not listed). */
  truncated: boolean
}

/**
 * The customer's credit: posted receipts and credit notes with something not yet applied or
 * refunded (the API's `hasOpenCredit` filter), oldest first.
 */
export function customerCreditsQueryOptions(customerId: string) {
  return queryOptions({
    queryKey: refundKeys.credits(customerId),
    queryFn: async ({ signal }): Promise<CustomerCredits> => {
      const [receipts, notes] = await Promise.all([
        api.get<Page<ReceiptListItem>>('/sales/receipts', {
          signal,
          query: { customerId, hasOpenCredit: true, pageSize: CREDITS_LIMIT, sort: '-receiptDate' },
        }),
        api.get<Page<CreditNoteListItem>>('/sales/credit-notes', {
          signal,
          query: { customerId, hasOpenCredit: true, pageSize: CREDITS_LIMIT, sort: '-noteDate' },
        }),
      ])
      const credits: CustomerCredit[] = [
        ...receipts.items.map((r) => ({
          sourceType: 'RECEIPT' as const,
          id: r.id,
          number: r.number,
          date: r.receiptDate,
          total: r.amount,
          refunded: r.refunded,
          available: r.unallocated,
        })),
        ...notes.items.map((n) => ({
          sourceType: 'CREDIT_NOTE' as const,
          id: n.id,
          number: n.number ?? '',
          date: n.noteDate,
          total: n.total,
          refunded: n.refunded,
          available: n.unallocated,
        })),
      ].filter((c) => !isZero(c.available) && !c.available.startsWith('-'))
      credits.sort((a, b) => a.date.localeCompare(b.date) || a.number.localeCompare(b.number))
      return {
        credits,
        truncated: receipts.total > receipts.items.length || notes.total > notes.items.length,
      }
    },
  })
}

export function useCreateRefund() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RefundInput) => api.post<CustomerRefund>('/sales/refunds', input),
    onSuccess: (refund) => {
      queryClient.setQueryData(refundKeys.detail(refund.id), refund)
      return invalidateSales(queryClient, { ledger: true })
    },
  })
}

export function useVoidRefund(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) => api.post<CustomerRefund>(path(id, 'void'), { reason }),
    onSuccess: (refund) => {
      queryClient.setQueryData(refundKeys.detail(refund.id), refund)
      return invalidateSales(queryClient, { ledger: true })
    },
  })
}
